/**
 * PositionPicker - Wrapper around generic EntityPicker
 * 
 * Provides position-specific configuration and maintains backward compatibility
 * with any code that imports PositionPickerField or PositionPickerModal
 */

import React, { useMemo } from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { FEATURES } from '../../../config/features';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── Position-specific configuration ──────────────────────────────────────────
export const POSITION_CONFIG = {
  apiEndpoint: "/api/Position/active",
  entityName: "Position",
  idField: "id",
  displayFields: {
    primary: "code",
    secondary: "description"
  },
  tableHeaders: [
    "Id",
    ...(FEATURES.POSITION_UNIT_CODE ? ["Unit Code"] : []),
    ...(FEATURES.POSITION_CODE ? ["Code"] : []),
    "Description"
  ],
  tableAccessors: [
    "id",
    ...(FEATURES.POSITION_UNIT_CODE ? ["unitCode"] : []), // Add conditional flag
    ...(FEATURES.POSITION_CODE ? ["code"] : []),
    "description"
  ],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Description", field: "description", index: 1 },
    { label: "Code", field: "code", index: 2 },
  ],
  placeholder: "--Select position--"
};


// ── PositionPickerSelect wrapper ───────────────────────────────────────────
/**
 * PositionPickerSelect
 * 
 * Usage:
 * <PositionPickerSelect
 *   label="Position"
 *   positionId={form.positionId}
 *   onSelect={(val) => setPosition(val)}
 *   placeholder="All Positions"
 * />
 */
export function PositionPickerSelect({
  label,
  departmentId,
  value,   // Only ID is required for display/matching
  onSelect,
  onClear,
  placeholder = "--Select position--",
  allowAll,
  disabled = false,
  isRequired = false,
  className = "",
  error = false,
  errorMessage = ""
}) {

  const config = useMemo(() => {
    let endpoint = POSITION_CONFIG.apiEndpoint;

    if (departmentId) {
      endpoint = `${endpoint}/department/${departmentId}`;
    }

    return {
      ...POSITION_CONFIG,
      apiEndpoint: endpoint,
    };
  }, [departmentId]);

  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <EntityPickerSelect
        config={config}
        // Pass an object with the ID. 
        // EntityPickerSelect matches this against its internal list.
        value={value ? { id: value } : null}
        disabled={disabled}
        placeholder={placeholder}
        allowAll={allowAll}
        className={className}
        onChange={(val) => {
          if (val === null) {
            if (onClear) onClear();
          } else {
            // Returns the full object {id, code, description}
            if (onSelect) onSelect(val);
          }
        }}
      />

      {/* Error message display */}
      {error && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
}


// ── PositionPickerField wrapper ──────────────────────────────────────────────
/**
 * PositionPickerField
 * 
 * Usage:
 * <PositionPickerField
 *   label="Position"
 *   positionId={form.positionId}
 *   displayValue={form.positionDescription}
 *   onSelect={(position) => {
 *     set("positionId", position.id);
 *     set("positionCode", position.code);
 *     set("positionDescription", position.description);
 *   }}
 *   onClear={() => {
 *     set("positionId", "");
 *     set("positionCode", "");
 *     set("positionDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function PositionPickerField({
  positionId,         // position-specific prop name
  departmentId,
  isDepending = false,
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {

  const config = useMemo(() => {
    let endpoint = POSITION_CONFIG.apiEndpoint;

    if (isDepending && departmentId) {
      endpoint = `${endpoint}/department/${departmentId}`;
    }

    return {
      ...POSITION_CONFIG,
      apiEndpoint: endpoint,
    };
  }, [departmentId]);

  return (
    <EntityPickerField
      {...rest}
      config={config}
      entityId={positionId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
      disabled={isDepending && !departmentId}
    />
  );
}

// ── PositionPickerModal wrapper ──────────────────────────────────────────────
/**
 * PositionPickerModal
 * 
 * Usage (standalone):
 * <PositionPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(position) => {
 *     set("positionId", position.id);
 *     set("positionDescription", position.description);
 *   }}
 * />
 */
export function PositionPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={POSITION_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { PositionPickerField as default };